/**
 * React Query hooks for admin user statistics and activity tracking
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../services/api';
import type { ActivityEventType } from '../types';

// =============================================================================
// Query Keys
// =============================================================================

export const userStatsKeys = {
  all: ['admin', 'user-stats'] as const,
  summary: (days: number) => [...userStatsKeys.all, 'summary', days] as const,
  groups: () => [...userStatsKeys.all, 'groups'] as const,
  activeUsers: (limit: number, days: number) =>
    [...userStatsKeys.all, 'active-users', limit, days] as const,
  trends: (days: number) => [...userStatsKeys.all, 'trends', days] as const,
  topCompanies: (limit: number, days: number) =>
    [...userStatsKeys.all, 'top-companies', limit, days] as const,
  topQuestions: (limit: number, days: number) =>
    [...userStatsKeys.all, 'top-questions', limit, days] as const,
};

export const activityKeys = {
  all: ['activity'] as const,
  session: () => [...activityKeys.all, 'session'] as const,
};

// =============================================================================
// User Statistics Hooks
// =============================================================================

/**
 * Get full user statistics summary
 */
export function useUserStatsSummary(days: number = 30) {
  return useQuery({
    queryKey: userStatsKeys.summary(days),
    queryFn: () => api.getAdminUserStatsSummary(days),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Get group statistics
 */
export function useGroupStats() {
  return useQuery({
    queryKey: userStatsKeys.groups(),
    queryFn: () => api.getAdminGroupStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get most active users
 */
export function useActiveUsers(limit: number = 10, days: number = 30) {
  return useQuery({
    queryKey: userStatsKeys.activeUsers(limit, days),
    queryFn: () => api.getAdminActiveUsers(limit, days),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Get DAU/WAU/MAU trend data
 */
export function useUserTrends(days: number = 30) {
  return useQuery({
    queryKey: userStatsKeys.trends(days),
    queryFn: () => api.getAdminUserTrends(days),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get top viewed companies
 */
export function useTopCompanies(limit: number = 10, days: number = 30) {
  return useQuery({
    queryKey: userStatsKeys.topCompanies(limit, days),
    queryFn: () => api.getAdminTopCompanies(limit, days),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get top chat questions
 */
export function useTopQuestions(limit: number = 10, days: number = 30) {
  return useQuery({
    queryKey: userStatsKeys.topQuestions(limit, days),
    queryFn: () => api.getAdminTopQuestions(limit, days),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// Activity Tracking Hooks
// =============================================================================

/**
 * Log an activity event
 */
export function useLogActivityEvent() {
  return useMutation({
    mutationFn: ({
      eventType,
      metadata,
      sessionId,
    }: {
      eventType: ActivityEventType;
      metadata?: Record<string, unknown>;
      sessionId?: string;
    }) => api.logActivityEvent(eventType, metadata, sessionId),
    // Don't invalidate any queries - activity logging is fire-and-forget
  });
}

/**
 * Start a new session
 */
export function useStartSession() {
  return useMutation({
    mutationFn: () => api.startSession(),
  });
}

/**
 * End a session
 */
export function useEndSession() {
  return useMutation({
    mutationFn: (sessionId: string) => api.endSession(sessionId),
  });
}

// =============================================================================
// Convenience Hook for Activity Tracking
// =============================================================================

/**
 * Hook that provides a simple trackEvent function for logging user activity.
 * This is a convenience wrapper for the mutation hook.
 *
 * @example
 * const { trackEvent } = useActivityTracking();
 * trackEvent('page_view', { page: '/companies' });
 */
export function useActivityTracking() {
  const logEvent = useLogActivityEvent();

  const trackEvent = (
    eventType: ActivityEventType,
    metadata?: Record<string, unknown>
  ) => {
    // Fire and forget - don't await
    logEvent.mutate({ eventType, metadata });
  };

  return {
    trackEvent,
    isLogging: logEvent.isPending,
    error: logEvent.error,
  };
}
